import { Modal, Select } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { ContactCard } from "./ContactCard.tsx"
import type { PortalProjectManager } from "../types/portal.types.ts"

export interface ContactProjectOption {
  id: string
  name: string
  contact: PortalProjectManager | null
}

interface ContactContractorModalProps {
  open: boolean
  onClose: () => void
  /** Single-project context: the contact is already known. */
  project?: ContactProjectOption
  /** All-projects context: the customer picks which project's team to
   * reach — never assume the last-opened project. */
  projectOptions?: ContactProjectOption[]
}

/** There is no messaging backend anywhere in the platform yet — this is the
 * honest working alternative the redesign brief asks for: real email/phone
 * for the customer's actual assigned contact, never a disabled button with
 * no explanation. Home's "Contact your contractor", the Messages page, and
 * Payments' "Ask a question" all open this same modal. */
export function ContactContractorModal({
  open,
  onClose,
  project,
  projectOptions
}: ContactContractorModalProps) {
  const { t } = useTranslation()
  const [chosenId, setChosenId] = useState<string | null>(null)

  const active = project ?? projectOptions?.find((p) => p.id === chosenId) ?? null

  const handleClose = () => {
    setChosenId(null)
    onClose()
  }

  return (
    <Modal open={open} onCancel={handleClose} footer={null} title={t("portal.contact.title")}>
      <div className="flex flex-col gap-4">
        {projectOptions && (
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              {t("portal.upload.projectLabel")}
            </label>
            <Select
              className="w-full"
              placeholder={t("portal.upload.chooseProject")}
              value={chosenId ?? undefined}
              onChange={setChosenId}
              options={projectOptions.map((p) => ({ value: p.id, label: p.name }))}
            />
          </div>
        )}

        {active && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="mb-3 text-sm text-gray-500">
              {t("portal.contact.messagingNotAvailable")}
            </p>
            <ContactCard contact={active.contact} />
          </div>
        )}
      </div>
    </Modal>
  )
}
